import {
  WORKFLOW_CATALOGUE,
  gatedModuleLabel,
  isPreviewModule,
  type WorkflowCondition,
  type WorkflowEdge,
  type WorkflowGraph,
  type WorkflowHolder,
  type WorkflowNode,
  type WorkflowParamDef,
  type WorkflowSubject,
  type WorkflowTarget,
  type WorkflowWait,
} from "@dentacore/shared-types";

import { useModules } from "../shell/modules-context";
import { ArrowCondition, ConditionFields } from "./condition-editor";
import {
  actionDef,
  defaultCondition,
  nodeAction,
  removalCost,
  stepNumbers,
  targetOptions,
  targetSubject,
  withAction,
} from "./graph-model";
import type { WorkflowRefs } from "./use-workflow-refs";
import styles from "./workflows.module.css";

/**
 * **The side panel, for the box or arrow picked on the canvas.** What a box
 * does, what it acts on and what it needs to be told; a fork's question; what
 * a wait waits for; an arrow's condition. The canvas box shows the action too,
 * so the common change is one click away without the panel.
 */
export function NodePanel({
  graph,
  subject,
  node,
  ring,
  hasTasks,
  refs,
  onChange,
  onRemove,
  onClose,
}: {
  graph: WorkflowGraph;
  subject: WorkflowSubject;
  node: WorkflowNode;
  ring: boolean;
  hasTasks: boolean;
  refs: WorkflowRefs;
  onChange: (node: WorkflowNode) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const number = stepNumbers(graph).get(node.id);
  const heading =
    node.kind === "fork"
      ? `Box ${String(number)} · Fork`
      : node.kind === "wait"
        ? `Box ${String(number)} · Wait`
        : `Step ${String(number)}`;
  const cost = removalCost(graph, node.id);

  return (
    <section className={styles.panelSection} data-testid="wf-node-panel" data-node-id={node.id}>
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>{heading}</h2>
        <button
          type="button"
          className={styles.quiet}
          onClick={onClose}
          data-testid="wf-node-panel-close"
        >
          Workflow details
        </button>
      </header>

      {node.kind === "action" && (
        <ActionFields
          graph={graph}
          subject={subject}
          node={node}
          ring={ring}
          hasTasks={hasTasks}
          refs={refs}
          onChange={onChange}
        />
      )}

      {node.kind === "fork" && (
        <>
          <ConditionFields
            subject={subject}
            value={node.condition ?? defaultCondition(subject)}
            onChange={(condition) => onChange({ ...node, condition })}
            refs={refs}
            testPrefix="wf-fork-cond"
          />
          <p className={styles.hint}>
            When this is true the run carries on along the line (“If yes”); otherwise it takes the
            branch below. Both branches need at least one box.
          </p>
        </>
      )}

      {node.kind === "wait" && (
        <WaitFields
          subject={subject}
          wait={node.wait ?? { kind: "for", amount: 1, unit: "days" }}
          refs={refs}
          onChange={(wait) => onChange({ ...node, wait })}
        />
      )}

      <div className={styles.panelFoot}>
        <button
          type="button"
          className={styles.dangerLink}
          onClick={() => onRemove(node.id)}
          data-testid="wf-node-panel-remove"
        >
          {node.kind === "fork"
            ? cost > 0
              ? `Remove this fork and its “otherwise” branch (${String(cost)} box${cost === 1 ? "" : "es"})`
              : "Remove this fork"
            : "Remove this box"}
        </button>
      </div>
    </section>
  );
}

function ActionFields({
  graph,
  subject,
  node,
  ring,
  hasTasks,
  refs,
  onChange,
}: {
  graph: WorkflowGraph;
  subject: WorkflowSubject;
  node: WorkflowNode;
  ring: boolean;
  hasTasks: boolean;
  refs: WorkflowRefs;
  onChange: (node: WorkflowNode) => void;
}): React.JSX.Element {
  const modules = useModules();
  const on = targetSubject(graph, subject, node.target);
  const def = nodeAction(graph, subject, node);
  const actions = on === null ? [] : WORKFLOW_CATALOGUE[on].actions;
  const targets = targetOptions(graph, subject, node.id);
  const targetKnown = targets.some((t) => t.value === (node.target ?? "self"));
  const moduleOff =
    def?.module === undefined
      ? false
      : isPreviewModule(def.module)
        ? !modules.hasPreview(def.module)
        : !modules.has(def.module);

  const actionLabel = (key: string): string => {
    const a = actionDef(on ?? subject, key);
    if (a === null) return key;
    return a.module === undefined || a.module === "TASKS"
      ? a.label
      : `${a.label} (${gatedModuleLabel(a.module)})`;
  };

  return (
    <div className={styles.stack}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Acts on</span>
        <select
          className={styles.input}
          value={node.target ?? "self"}
          onChange={(event) => {
            const target = event.target.value as WorkflowTarget;
            const next = targetSubject(graph, subject, target);
            const keep = next !== null && actionDef(next, node.action) !== null;
            onChange({
              ...node,
              target,
              ...(keep
                ? {}
                : {
                    action: next === null ? undefined : WORKFLOW_CATALOGUE[next].actions[0]?.key,
                    params: {},
                  }),
            });
          }}
          data-testid="wf-node-target"
        >
          {!targetKnown && (
            <option value={node.target}>A record that is no longer before this step</option>
          )}
          {targets.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Does</span>
        <select
          className={styles.input}
          value={def === null ? "" : (node.action ?? "")}
          onChange={(event) => onChange(withAction(graph, subject, node, event.target.value))}
          data-testid="wf-node-action"
        >
          {def === null && <option value="">Pick an action</option>}
          <optgroup label="Runs on its own">
            {actions
              .filter((a) => a.kind === "auto")
              .map((a) => (
                <option key={a.key} value={a.key}>
                  {actionLabel(a.key)}
                </option>
              ))}
          </optgroup>
          <optgroup label="Someone does it">
            {actions
              .filter((a) => a.kind === "person")
              .map((a) => (
                <option key={a.key} value={a.key}>
                  {actionLabel(a.key)}
                </option>
              ))}
          </optgroup>
        </select>
      </label>

      {def !== null && (
        <p className={styles.hint}>
          {def.kind === "auto"
            ? ring
              ? "Runs on its own, with the permissions of whoever started the run."
              : "Runs on its own, as the workflow’s role."
            : ring
              ? "Someone does it: the ring asks the person who started the run, who does it or skips it."
              : "Someone does it: it becomes a task for the role below, and the run waits. Resolved, the run carries on; closed as not needed, it stops."}
          {def.danger === true ? " It cannot be undone by the workflow." : ""}
        </p>
      )}
      {moduleOff && def?.module !== undefined && (
        <p className={styles.warnText} role="note">
          Needs {gatedModuleLabel(def.module)}, which this clinic does not have on — the workflow
          will be refused until it is.
        </p>
      )}

      {def?.kind === "person" && !ring && (
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Its task goes to</span>
          <RoleSelect
            refs={refs}
            value={node.roleId ?? null}
            onChange={(roleId) => onChange({ ...node, roleId })}
            testId="wf-node-role"
          />
          {!hasTasks && (
            <span className={styles.warnText}>
              Without the Tasks module there is nowhere to send it — start this workflow from the
              ring, or switch Tasks on.
            </span>
          )}
        </label>
      )}

      {def?.params?.map((param) => (
        <ParamField
          key={param.key}
          param={param}
          value={node.params?.[param.key]}
          refs={refs}
          onChange={(value) =>
            onChange({ ...node, params: { ...(node.params ?? {}), [param.key]: value } })
          }
        />
      ))}
    </div>
  );
}

function RoleSelect({
  refs,
  value,
  onChange,
  testId,
  placeholder = "Pick a role",
}: {
  refs: WorkflowRefs;
  value: string | null;
  onChange: (roleId: string | null) => void;
  testId: string;
  placeholder?: string;
}): React.JSX.Element {
  return (
    <select
      className={styles.input}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value === "" ? null : event.target.value)}
      data-testid={testId}
    >
      <option value="">{placeholder}</option>
      {(refs.roles ?? []).map((role) => (
        <option key={role.id} value={role.id}>
          {role.name}
        </option>
      ))}
      {value !== null && refs.roles !== null && !refs.roles.some((r) => r.id === value) && (
        <option value={value}>A role you cannot see</option>
      )}
    </select>
  );
}

export { RoleSelect };

function ParamField({
  param,
  value,
  refs,
  onChange,
}: {
  param: WorkflowParamDef;
  value: string | number | WorkflowHolder | null | undefined;
  refs: WorkflowRefs;
  onChange: (value: string | number | WorkflowHolder | null) => void;
}): React.JSX.Element {
  const label = (
    <span className={styles.fieldLabel}>
      {param.label}
      {param.required ? "" : " (optional)"}
    </span>
  );
  const testId = `wf-param-${param.key}`;
  if (param.type === "holder") {
    // People need Tasks' own list; a role is what a workflow's task usually goes to (WG-D2).
    const roleId =
      value !== null && typeof value === "object" && "roleId" in value ? value.roleId : null;
    const person = value !== null && typeof value === "object" && "userId" in value;
    return (
      <label className={styles.field}>
        {label}
        <RoleSelect
          refs={refs}
          value={roleId}
          onChange={(id) => onChange(id === null ? null : { roleId: id })}
          testId={testId}
          placeholder={person ? "A person (kept as it is)" : "Pick a role"}
        />
        <span className={styles.hint}>Anyone with this role at the location can take it.</span>
      </label>
    );
  }
  if (param.type === "longtext")
    return (
      <label className={styles.field}>
        {label}
        <textarea
          className={styles.input}
          rows={3}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          data-testid={testId}
        />
      </label>
    );
  if (param.type === "hours")
    return (
      <label className={styles.field}>
        {label}
        <input
          className={styles.input}
          type="number"
          min={0}
          step={1}
          value={typeof value === "number" ? value : ""}
          onChange={(event) =>
            onChange(
              event.target.value === ""
                ? null
                : Math.max(0, Math.round(Number(event.target.value))),
            )
          }
          data-testid={testId}
        />
      </label>
    );
  return (
    <label className={styles.field}>
      {label}
      <input
        className={styles.input}
        type="text"
        maxLength={200}
        value={typeof value === "string" ? value : ""}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      />
    </label>
  );
}

function WaitFields({
  subject,
  wait,
  refs,
  onChange,
}: {
  subject: WorkflowSubject;
  wait: WorkflowWait;
  refs: WorkflowRefs;
  onChange: (wait: WorkflowWait) => void;
}): React.JSX.Element {
  return (
    <div className={styles.stack}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Wait</span>
        <select
          className={styles.input}
          value={wait.kind}
          onChange={(event) => {
            const kind = event.target.value;
            onChange(
              kind === "for"
                ? { kind: "for", amount: 1, unit: "days" }
                : kind === "until"
                  ? { kind: "until", at: "09:00", day: "tomorrow" }
                  : {
                      kind: "field",
                      condition: defaultCondition(subject),
                      giveUp: { amount: 2, unit: "days" },
                    },
            );
          }}
          data-testid="wf-wait-kind"
        >
          <option value="for">For a length of time</option>
          <option value="until">Until a time of day</option>
          <option value="field">Until something is true</option>
        </select>
      </label>

      {wait.kind === "for" && (
        <div className={styles.row2}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>How long</span>
            <input
              className={styles.input}
              type="number"
              min={1}
              step={1}
              value={wait.amount}
              onChange={(event) =>
                onChange({
                  ...wait,
                  amount: Math.max(1, Math.round(Number(event.target.value) || 1)),
                })
              }
              data-testid="wf-wait-amount"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Unit</span>
            <select
              className={styles.input}
              value={wait.unit}
              onChange={(event) =>
                onChange({ ...wait, unit: event.target.value as "minutes" | "hours" | "days" })
              }
              data-testid="wf-wait-unit"
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </label>
        </div>
      )}

      {wait.kind === "until" && (
        <div className={styles.row2}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>At</span>
            <input
              className={styles.input}
              type="time"
              value={wait.at}
              onChange={(event) => onChange({ ...wait, at: event.target.value })}
              data-testid="wf-wait-at"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Day</span>
            <select
              className={styles.input}
              value={wait.day}
              onChange={(event) =>
                onChange({ ...wait, day: event.target.value as "today" | "tomorrow" })
              }
              data-testid="wf-wait-day"
            >
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
            </select>
          </label>
        </div>
      )}

      {wait.kind === "field" && (
        <>
          <ConditionFields
            subject={subject}
            value={wait.condition}
            onChange={(condition: WorkflowCondition) => onChange({ ...wait, condition })}
            refs={refs}
            testPrefix="wf-wait-cond"
          />
          <div className={styles.row2}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Give up after</span>
              <input
                className={styles.input}
                type="number"
                min={1}
                step={1}
                value={wait.giveUp.amount}
                onChange={(event) =>
                  onChange({
                    ...wait,
                    giveUp: {
                      ...wait.giveUp,
                      amount: Math.max(1, Math.round(Number(event.target.value) || 1)),
                    },
                  })
                }
                data-testid="wf-wait-giveup-amount"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Unit</span>
              <select
                className={styles.input}
                value={wait.giveUp.unit}
                onChange={(event) =>
                  onChange({
                    ...wait,
                    giveUp: { ...wait.giveUp, unit: event.target.value as "hours" | "days" },
                  })
                }
                data-testid="wf-wait-giveup-unit"
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </label>
          </div>
          <p className={styles.hint}>If it is still not true by then, the run stops and says so.</p>
        </>
      )}
      {wait.kind === "until" && <p className={styles.hint}>In each location’s own time zone.</p>}
    </div>
  );
}

export function EdgePanel({
  graph,
  subject,
  edge,
  refs,
  onChange,
  onClose,
}: {
  graph: WorkflowGraph;
  subject: WorkflowSubject;
  edge: WorkflowEdge;
  refs: WorkflowRefs;
  onChange: (edge: WorkflowEdge) => void;
  onClose: () => void;
}): React.JSX.Element {
  const from = graph.nodes.find((n) => n.id === edge.from);
  const numbers = stepNumbers(graph);
  const to = numbers.get(edge.to);
  return (
    <section className={styles.panelSection} data-testid="wf-edge-panel" data-edge-id={edge.id}>
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Arrow into box {String(to ?? "")}</h2>
        <button
          type="button"
          className={styles.quiet}
          onClick={onClose}
          data-testid="wf-edge-panel-close"
        >
          Workflow details
        </button>
      </header>
      <ArrowCondition
        subject={subject}
        value={edge.condition}
        first={from?.kind === "start"}
        onChange={(condition) => onChange({ ...edge, condition })}
        refs={refs}
      />
    </section>
  );
}
